import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useAuth } from "../auth/AuthContext";
import { ReviewAlert } from "./ReviewAlert";
import { BgVideo } from "./BgVideo";
import { useRoles } from "../lib/useRoles";
import { api } from "../api/client";

/** Fire a page-view activity event on each route change (authenticated only). */
function usePageViewTracking(isAuthenticated: boolean): void {
  const location = useLocation();
  const last = useRef<string>("");
  useEffect(() => {
    if (!isAuthenticated) return;
    const path = location.pathname + location.search;
    if (path === last.current) return; // de-dupe repeated renders
    last.current = path;
    void api.trackActivity(path, document.title);
  }, [isAuthenticated, location.pathname, location.search]);
}

/**
 * On first login, route a brand-new parent (no teams AND no wrestlers) to the
 * onboarding wizard — once per session. Honors a "skip" flag so the user isn't
 * trapped if they chose to skip, and never redirects away from onboarding or
 * the auth callback.
 */
function useFirstLoginOnboarding(isAuthenticated: boolean): void {
  const navigate = useNavigate();
  const location = useLocation();
  const checked = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || checked.current) return;
    const path = location.pathname;
    // Don't interfere with onboarding itself, auth flow, or public pages.
    if (
      path.startsWith("/onboarding") ||
      path.startsWith("/auth") ||
      path === "/" ||
      path.startsWith("/i/") ||
      path.startsWith("/join/")
    ) {
      return;
    }
    if (sessionStorage.getItem("onboardingChecked") === "1") {
      checked.current = true;
      return;
    }
    checked.current = true;
    sessionStorage.setItem("onboardingChecked", "1");
    void (async () => {
      try {
        const [me, wrestlers] = await Promise.all([
          api.getMe(),
          api.listWrestlers(),
        ]);
        const noTeams = (me.teams?.length ?? 0) === 0;
        const noKids = (wrestlers.items?.length ?? 0) === 0;
        if (noTeams && noKids) navigate("/onboarding");
      } catch {
        // Non-fatal: never block the app on this check.
      }
    })();
  }, [isAuthenticated, location.pathname, navigate]);
}

export function AppShell() {
  const { isAuthenticated, email, isAdmin, logout } = useAuth();
  usePageViewTracking(isAuthenticated);
  useFirstLoginOnboarding(isAuthenticated);
  const { anyCoach, teams } = useRoles();
  const hasTeams = teams.length > 0;

  // Unauthenticated (sign-in): render only the content so the full-screen
  // video fills the viewport with no nav/padding chrome.
  if (!isAuthenticated) {
    return <Outlet />;
  }

  return (
    <div className="app-shell">
      {/* Ambient wrestling b-roll, heavily obscured by .app-bg overlay. */}
      <BgVideo />
      {/* Fixed app background image during normal (signed-in) use. */}
      <div className="app-bg" aria-hidden="true" />
      <nav className="nav">
        <Link to="/" className="brand">
          <img
            src="/media/crest-128.webp"
            alt=""
            aria-hidden="true"
            className="brand-crest"
          />
          Meet Tracker
        </Link>
        <NavLink to="/meets" className="nav-link">
          My Meets
        </NavLink>
        <NavLink to="/dashboard" className="nav-link">
          Dashboard
        </NavLink>
        <NavLink to="/discover" className="nav-link">
          Discover
        </NavLink>
        <NavLink to="/record" className="nav-link">
          Records
        </NavLink>
        <NavLink to="/standings" className="nav-link">
          Standings
        </NavLink>
        <NavLink to="/profile" className="nav-link">
          Profile
        </NavLink>
        {isAdmin && (
          <>
            <NavLink to="/teams" className="nav-link">
              Teams
            </NavLink>
            <NavLink to="/admin/users" className="nav-link">
              Users
            </NavLink>
            <NavLink to="/admin/activity" className="nav-link">
              Activity
            </NavLink>
            <NavLink to="/discovery/sources" className="nav-link">
              Sources
            </NavLink>
            <NavLink to="/discovery/review" className="nav-link">
              Review
            </NavLink>
          </>
        )}
        <span className="nav-roles" style={{ marginLeft: "auto" }}>
          {isAdmin && <span className="badge accent" title="Platform admin">Admin</span>}
          {anyCoach && <span className="badge" title="You coach at least one team">Coach</span>}
          {hasTeams && !anyCoach && (
            <span className="badge" title="Team parent">Parent</span>
          )}
        </span>
        <span className="muted nav-email">
          {email}
        </span>
        <button className="secondary btn-lift" onClick={logout}>
          Sign out
        </button>
      </nav>
      <ReviewAlert />
      <main className="container">
        <Outlet />
      </main>
    </div>
  );
}
